/**
 * Pure time-display helpers — extracted from FacebookPanel and NewsTicker
 * (which had two slightly-different `ago()` implementations) into a single
 * canonical version.
 *
 * Uses lowercase labels matching the NewsTicker convention ("now", "m", "h",
 * "d") and adds a NaN guard from the Facebook version. Returns "—" for
 * invalid ISO strings rather than silently showing NaN arithmetic.
 */

/**
 * Format an ISO datetime string as a short human-readable age.
 *
 * @returns "now" (< 1 min), "Nm" (minutes), "Nh" (hours), "Nd" (days),
 *          or "—" for invalid/null input.
 */
/**
 * Format a Date as a short uppercase date string: "15 JAN 2025".
 * Used in the TopBar clock display.
 */
export function formatDate(d: Date): string {
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
}

export function ago(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const ms = Date.now() - t;
  const m = Math.round(ms / 60_000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

/**
 * Format an observation time the way the panel needs it: "22 OCT 05:00 ICT".
 *
 * The raw ISO string is 25 characters and wraps onto a second line in the
 * 302 px rail — nine air-station rows spent 1,100 px of a 9,000 px column
 * largely on timestamps nobody could read at a glance.
 */
export function fmtObservation(iso: string | null | undefined): string {
  if (!iso) return "time unknown";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "time unknown";
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "Asia/Bangkok" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Bangkok" });
  return `${date.toUpperCase()} ${time} ICT`;
}

/** Open-Meteo Bangkok local time; preserve an explicit offset when supplied. */
export function bangkokForecastTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const iso = /(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? value : `${value}+07:00`;
  return Number.isFinite(Date.parse(iso)) ? iso : null;
}
